/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { REPORT_CONSTRUCTION_SCHEDULE, ReportService } from '@gh/builder-shared';
import {
  ConstructionScheduleRestService,
  MaintenanceScheduleRestService,
  PreConstructionScheduleRestService,
  reportField,
  reportIdField,
  SalesScheduleRestService,
  ScheduleTaskType,
  ScheduleType,
} from '@gh/builder-shared-data';
import {
  DictionaryService,
  getFormStateSnapshotValue,
  getRouteParam,
  getRouteQueryParam,
  getRouteStaticData,
  InitViewService,
  isRouteForCreationMode,
} from '@gh/common-shared';
import { MessageBasket } from '@gh/core-messages';
import { FormMode } from '@gh/core-ui';
import { GridLayoutService } from '@gh/core-ui-grid';
import { throwError$ } from '@gh/rx';
import { map$, switchMap$ } from '@gh/rx/operators';

import { isNil } from 'lodash';
import { Observable } from 'rxjs';

import {
  ScheduleDetails,
  scheduleDetailsFromDto,
  scheduleDetailsToDto,
} from './schedule-details.model';

type ScheduleRestService = ConstructionScheduleRestService
  | SalesScheduleRestService
  | PreConstructionScheduleRestService
  | MaintenanceScheduleRestService;

@Injectable()
export class ScheduleDetailsBusinessService implements InitViewService {
  constructor(private dictionaries: DictionaryService,
              private gridLayouts: GridLayoutService,
              private constructionScheduleRest: ConstructionScheduleRestService,
              private salesScheduleRest: SalesScheduleRestService,
              private preConstructionScheduleRest: PreConstructionScheduleRestService,
              private maintenanceScheduleRest: MaintenanceScheduleRestService,
              private reports: ReportService) {
  }

  initView(route: ActivatedRouteSnapshot): any {
    const type = getRouteStaticData(route)['type'];
    const restService = this.selectRestService(type);
    const gridLayoutName = {
      [ScheduleType.Construction]: 'constructionScheduleTasks',
      [ScheduleType.Sales]: 'salesScheduleTasks',
      [ScheduleType.PreConstruction]: 'preconstructionScheduleTasks',
      [ScheduleType.Maintenance]: 'maintenanceScheduleTasks',
    }[type];

    const scheduleId = getRouteParam(route, 'scheduleId');

    const schedule$ = isRouteForCreationMode(route)
      ? restService.init(
        getRouteQueryParam(route, 'jobId') || getRouteQueryParam(route, 'salesFolderId'),
        getRouteQueryParam(route, 'templateId'),
      ).pipe(map$(scheduleDetailsFromDto))
      : restService.get(scheduleId).pipe(map$(scheduleDetailsFromDto));

    // call additional validation only if it is a construction schedule
    const scheduleWithValidation$ = type === ScheduleType.Construction
      ? schedule$.pipe(switchMap$((schedule) => isNil(getFormStateSnapshotValue(schedule.masterInfo).authToCommenceDate)
        ? throwError$(MessageBasket.error('msg_no_auth_to_commence'))
        : [schedule]))
      : schedule$;

    return {
      schedule: scheduleWithValidation$,
      taskTypeDict: this.dictionaries.getByEnum(ScheduleTaskType),
      taskGridLayout: this.gridLayouts.get(gridLayoutName),
      reportUrl: type === ScheduleType.Construction && !isRouteForCreationMode(route)
        ? this.reports.generateUrl(REPORT_CONSTRUCTION_SCHEDULE, [
          reportIdField('ScheduleId', scheduleId),
          reportField('IsFromBuilderPortal', true),
        ]) : void 0,
    };
  }

  save(schedule: ScheduleDetails, formMode: FormMode): Observable<ScheduleDetails> {
    const restService = this.selectRestService(schedule.type);
    return schedule.scheduleId
      ? restService.update(scheduleDetailsToDto(schedule, formMode)).pipe(map$(scheduleDetailsFromDto))
      : restService.create(scheduleDetailsToDto(schedule, formMode)).pipe(map$(scheduleDetailsFromDto));
  }

  recalculate(schedule: ScheduleDetails): Observable<ScheduleDetails> {
    return this.constructionScheduleRest.recalculate(scheduleDetailsToDto(schedule)).pipe(
      map$(scheduleDetailsFromDto));
  }

  private selectRestService(type: ScheduleType): ScheduleRestService {
    return {
      [ScheduleType.Construction]: this.constructionScheduleRest,
      [ScheduleType.Sales]: this.salesScheduleRest,
      [ScheduleType.PreConstruction]: this.preConstructionScheduleRest,
      [ScheduleType.Maintenance]: this.maintenanceScheduleRest,
    }[type];
  }
}
